import { profileColors } from 'fitbeat.models';
import { Button, ButtonTypes, FormError, IAppStore, InitialsCircle } from 'fitbeat.react-native-components';
import React, {Component} from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import CogIcon from '../assets/icons/cog.svg';
import SwitchIcon from '../assets/icons/switch.svg';
import TrashIcon from '../assets/icons/trash.svg';
import { truncate } from '../HomeWorkouts/components/Music/SongListItem';
import Theme from '../theme';
import { defaultScale, scaleText } from '../utils/scaling';
import UserDataConsumer from '../utils/UserDataConsumer';
import {
  cancelPendingInvitation,
  loadConfirmCancelScreen,
  loadInviteMemberScreen,
  loadMemberSettingsScreen,
  loadSwitchMemberScreen,
  manageMembers,
  manageMembersDone,
  swopUser,
} from './actions';
import { IPendingInvite, ISwitchMemberUserProfile } from './reducer';
import {
  activeFamilyMemberSelector,
  cancelPendingInviteErrorSelector,
  familyMembersSelector,
  isManagingMembersSelector,
  isSwitchingToUserSelector,
  pendingInvitesSelector,
  switchMemberScreenLoadingSelector,
} from './selector';

const isWeb = Platform.OS === 'web';

interface IProps {
  t: any;
  familyMembers: ISwitchMemberUserProfile[];
  pendingInvites: IPendingInvite[];
  isLoading: boolean;
  isSwitchingToUser: boolean;
  activeFamilyMember: ISwitchMemberUserProfile;
  cancelPendingInviteError: string | undefined;
  isManagingMembers: boolean;
  loadSwitchMemberScreen: () => void;
  loadInviteMemberScreen: () => void;
  loadMemberSettingsScreen: (userProfile: ISwitchMemberUserProfile) => void;
  swopUser: (swoppingToUser: ISwitchMemberUserProfile) => void;
  cancel: (pendingInvite: IPendingInvite) => void;
  manageMembers: () => void;
  manageMembersDone: () => void;
  removeMember: (userProfile: ISwitchMemberUserProfile) => void;
}

class SwitchMember extends Component<IProps> {
  constructor(props) {
    super(props);
  }

  public componentDidMount() {
    this.props.loadSwitchMemberScreen();
  }

  public render() {
    const { t } = this.props;

    const renderPendingInvites = () => {
      if (this.props.pendingInvites.length > 0) {
        return (
          <View style={{marginTop: defaultScale(20)}}>
            <Text style={styles.invitesPendingFontStyle}>
              {this.props.pendingInvites.length}{' '}
              {this.props.pendingInvites.length === 1
                ? t('invitePending')
                : t('invitesPending')}
            </Text>
            <FormError error={this.props.cancelPendingInviteError} color={Theme.GREY_DARK} />
            <FlatList
            data={this.props.pendingInvites}
            keyExtractor={(user) => user.code}
            contentContainerStyle={{ paddingBottom: defaultScale(10) }}
            renderItem={({ item }) => {
              return (
                <View style={styles.pendingInvitesContainer}>
                  <View style={styles.pendingInvitesContent}>
                    <Text style={styles.pendingInviteInformationFontStyle}>{
                      truncate(item.email, defaultScale(35))
                    }</Text>
                    {item.isCancellingInvite ? (
                      <Text style={{...styles.cancelPendingInviteFontStyle, color: Theme.GREY_MEDIUM}}>{t('cancelling')}</Text>
                    ) : (
                      <Text onPress={() => this.props.cancel(item)} style={styles.cancelPendingInviteFontStyle}>{t('cancel')}</Text>
                    )}
                  </View>
                </View>
              )
            }}
            />
          </View>
        );
      }
    };

    const handleOnMemberPress = (item: ISwitchMemberUserProfile) => {
      if (this.props.isManagingMembers) {
        item.isCurrentLoggedInUser ? this.props.loadMemberSettingsScreen(item) : this.props.removeMember(item);
        return;
      }

      if (!this.props.isManagingMembers) {
        item.isCurrentLoggedInUser ? this.props.loadMemberSettingsScreen(item) : this.props.swopUser(item);
        return;
      }
    };

    return (
      <SafeAreaView style={styles.container}>
        {this.props.isLoading && (
          <ActivityIndicator
            size='small'
            color={Theme.PRIMARY}
            animating={true}
            style={styles.loader}
          />
        )}

        {!this.props.isLoading && (
        <View style={styles.contentContainer}>
          <Text style={styles.mainHeader}>{this.props.isManagingMembers ? t('manageMembers') : t('switchMember')}</Text>
          <ScrollView>
              <>
                {this.props.familyMembers.map((item) => {
                  const profileColor =  item.familyPlanSettings ?
                    item.familyPlanSettings.profileColor : profileColors[0];
                  return (
                    <TouchableWithoutFeedback
                      disabled={this.props.isSwitchingToUser}
                      onPress={() => handleOnMemberPress(item)}
                    >
                      <View
                        style={
                          item.isCurrentLoggedInUser
                            ? {
                                ...styles.memberContainer,
                                backgroundColor: Theme.DARK_CHARCOAL,
                              }
                            : this.props.isSwitchingToUser
                            ? { ...styles.memberContainer, opacity: 0.3 }
                            : styles.memberContainer
                        }
                      >
                        <View style={styles.userDetailContainer}>
                          <View style={styles.userIconContainer}>
                            <InitialsCircle
                              initials={item.initials}
                              backgroundColor={profileColor ?? profileColors[0]}
                              color={Theme.WHITE}
                              size={defaultScale(40)}
                              containerStyle={styles.userIconContainer}
                              textStyle={styles.userIconText}
                            />
                            <View style={styles.userDetail}>
                              {this.props.isManagingMembers ? (
                                <View>
                                  <View style={{ flexDirection: 'row' }}>
                                    <Text
                                      style={
                                        item.isCurrentLoggedInUser
                                          ? {
                                              ...styles.userDetailText,
                                              color: Theme.WHITE,
                                            }
                                          : styles.userDetailText
                                      }
                                    >{item.firstname}</Text>
                                    {item.isFamilyPlanAdmin && (
                                      <View style={{flex: 1, justifyContent: 'flex-end'}}>
                                        <Text style={styles.userStatusText}>{t('owner')}</Text>
                                      </View>
                                    )}
                                  </View>
                                  <Text style={
                                    item.isCurrentLoggedInUser
                                      ? {
                                          ...styles.userEmailText,
                                          color: Theme.WHITE,
                                        }
                                      : styles.userEmailText}>{item.email}</Text>
                                </View>
                              ) : (
                                <Text
                                  style={
                                    item.isCurrentLoggedInUser
                                      ? {
                                          ...styles.userDetailText,
                                          color: Theme.WHITE,
                                        }
                                      : styles.userDetailText
                                  }
                                >
                                  {item.firstname}
                                </Text>
                              )}
                            </View>
                          </View>
                          <View style={styles.settingsIcon}>
                            {this.props.isSwitchingToUser &&
                            item.isCurrentLoggedInUser ? (
                              <Text style={styles.switching}>
                                {t('switching')}...
                              </Text>
                            ) : (
                              <>
                                {item.isCurrentLoggedInUser && (
                                  <CogIcon
                                    width={defaultScale(22)}
                                    height={defaultScale(22)}
                                    color={Theme.WHITE}
                                  />
                                )}
                                {!item.isCurrentLoggedInUser &&
                                  !this.props.isManagingMembers && (
                                    <SwitchIcon
                                      color={Theme.BLACK}
                                      width={defaultScale(22)}
                                      height={defaultScale(22)}
                                    />
                                  )}
                                {!item.isCurrentLoggedInUser &&
                                  this.props.isManagingMembers && (
                                    <TrashIcon
                                      color={Theme.BLACK}
                                      width={defaultScale(22)}
                                      height={defaultScale(22)}
                                    />
                                  )}
                              </>
                            )}
                          </View>
                        </View>
                      </View>
                    </TouchableWithoutFeedback>
                  );
                })}
                {renderPendingInvites()}
              </>
            </ScrollView>
          {this.props.activeFamilyMember?.isFamilyPlanAdmin ? (
            <View
              style={this.props.isSwitchingToUser ? { opacity: 0.3 } : {}}
            >
              <View>
                <Button
                  title={t('inviteFamilyMember')}
                  onPress={() => this.props.loadInviteMemberScreen()}
                  buttonType={ButtonTypes.OUTLINE}
                  contentContainerStyle={styles.invite}
                  overrideFontStyles={[styles.inviteFontStyle, { color: Theme.PRIMARY }]}
                />
              </View>
              {this.props.isManagingMembers ? (
                <View style={styles.doneManageContainer}>
                  <Button
                    title={t('done')}
                    buttonType={ButtonTypes.NORMAL}
                    contentContainerStyle={styles.mainAction}
                    overrideFontStyles={styles.mainActionFontStyle}
                    onPress={() => this.props.manageMembersDone()}
                  />
                </View>

              ) : (
                <Text onPress={() => this.props.manageMembers()} style={[styles.manage, { color: Theme.PRIMARY }]}>
                  {t('manageMembers')}
                </Text>
              )}
            </View>
          ) : undefined}
        </View>
        )}
      </SafeAreaView>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.WHITE,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: defaultScale(15),
  },
  mainHeader: {
    color: Theme.CHARCOAL,
    lineHeight: defaultScale(25),
    fontSize: defaultScale(24),
    fontFamily: Theme.Custom700,
    paddingTop: defaultScale(15),
  },
  memberContainer: {
    paddingVertical: defaultScale(15),
    borderWidth: defaultScale(0.3),
    borderColor: Theme.GREY_DARK,
    marginTop: defaultScale(15),
    flexDirection: 'row',
    borderRadius: defaultScale(3),
    height: defaultScale(70),
  },
  userDetailContainer: {
    flex: 1,
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  userIconContainer: {
    flexDirection: 'row',
    marginLeft: defaultScale(10),
  },
  userIconText: {
    color: Theme.WHITE,
    fontFamily: Theme.Weight700,
    fontSize: defaultScale(16),
  },
  userDetail: {
    justifyContent: 'center',
  },
  userDetailText: {
    ...Theme.textStyle.bodyCopyBold,
    marginLeft: defaultScale(15),
  },
  userStatusText: {
    ...Theme.textStyle.bodyCopyReg,
    justifyContent: 'flex-end',
    marginLeft: defaultScale(5),
    color: '#747474',
  },
  userEmailText: {
    ...Theme.textStyle.small,
    marginLeft: defaultScale(15),
  },
  settingsIcon: {
    alignSelf: 'center',
    marginVertical: isWeb ? 10 : defaultScale(3),
    marginRight: defaultScale(20),
  },
  doneManageContainer: {
    marginBottom: defaultScale(15),
    marginTop: defaultScale(15),
  },
  invite: {
    alignItems: 'center',
    borderColor: Theme.PRIMARY,
    borderRadius: defaultScale(5),
  },
  inviteFontStyle: {
    fontFamily: Theme.Weight700,
    fontSize: scaleText(16),
  },
  manage: {
    textAlign: 'center',
    marginTop: defaultScale(15),
    marginBottom: defaultScale(15),
    fontFamily: Theme.Weight700,
    fontSize: scaleText(16),
  },
  loader: {
    flex: 1,
    paddingVertical: defaultScale(20),
    alignItems: 'center',
  },
  swoppingToUserLoader: {
    flex: 1,
    alignItems: 'center',
  },
  switching: {
    fontFamily: Theme.Weight100Italic,
    fontSize: defaultScale(16),
    color: Theme.WHITE,
  },
  pendingInvitesContainer: {
    backgroundColor: Theme.WHITE,
    borderWidth: 1,
    borderColor: Theme.GREY_MEDIUM_LIGHT,
    marginBottom: defaultScale(5)
  },
  invitesPendingFontStyle: {
    fontFamily: Theme.Weight700,
    fontSize: defaultScale(16),
    marginBottom: defaultScale(16)
  },
  pendingInvitesContent: {
    flexDirection: 'row',
    padding: defaultScale(10),
    justifyContent: 'space-between'
  },
  pendingInviteInformationFontStyle: {
    fontFamily: Theme.Weight400,
    fontSize: defaultScale(16),
  },
  cancelPendingInviteFontStyle: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.PRIMARY,
  },
  mainAction: {
    borderColor: Theme.PRIMARY,
    borderRadius: defaultScale(5),
    alignItems: 'center',
  },
  mainActionFontStyle: {
    fontFamily: Theme.Weight700,
    fontSize: scaleText(16),
    color: Theme.WHITE,
  },
});

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  return {
    familyMembers: familyMembersSelector(state),
    isLoading: switchMemberScreenLoadingSelector(state),
    isSwitchingToUser: isSwitchingToUserSelector(state),
    activeFamilyMember: activeFamilyMemberSelector(state),
    pendingInvites: pendingInvitesSelector(state),
    cancelPendingInviteError: cancelPendingInviteErrorSelector(state),
    isManagingMembers: isManagingMembersSelector(state),
  };
};

const mapDispatchToProps = (dispatch: any, ownProps: any) => {
  const userServiceClient = ownProps.user.userServiceClient;
  const userProfileService = ownProps.user.userProfileService;
  const setUpInitialUserData = ownProps.user.setUpInitialUserData;
  const navigation = ownProps.navigation;
  const userProfile = ownProps.user.userProfile;

  return {
    loadSwitchMemberScreen: () => dispatch(loadSwitchMemberScreen(userProfile, userServiceClient)),
    swopUser: (swoppingToUser: ISwitchMemberUserProfile) =>
      dispatch(swopUser(swoppingToUser, userProfile.uid, userServiceClient,
        userProfileService, navigation, setUpInitialUserData)),
    loadInviteMemberScreen: () => dispatch(loadInviteMemberScreen(navigation)),
    loadMemberSettingsScreen: (userProfile: ISwitchMemberUserProfile) =>
      dispatch(loadMemberSettingsScreen(navigation, userProfile)),
    cancel: (pendingInvite: IPendingInvite) => dispatch(cancelPendingInvitation(userServiceClient, pendingInvite)),
    manageMembers: () => dispatch(manageMembers()),
    manageMembersDone: () => dispatch(manageMembersDone()),
    removeMember: (userProfile: ISwitchMemberUserProfile) => dispatch(loadConfirmCancelScreen(navigation, userProfile, true)),
  };
};

const switchMember = translate(['familyPlan'], { wait: true })(SwitchMember);
export default UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(switchMember));
